<template>
  <div class="wrap">
    <div class="left-side">
      <div
        v-if="description !== ''"
        v-dompurify-html="description"
        class="description"
      />
    </div>
    <div
      class="form-wrap"
      :style="getFormBackgroundImage()"
    >
      <form ref="refName">
        <h3 v-if="title !== ''">{{ title }}</h3>
        <transition
          name="slideInOut"
          mode="out-in"
        >
          <form-errors v-if="formErrors.length">
            <li
              v-for="(errorMessage, index) in formErrors"
              :key="index"
              v-dompurify-html="$t(errorMessage)"
            />
          </form-errors>
        </transition>

        <div class="fields">
          <div :class="['input-block', { error: $v.email.$error }]">
            <div class="label-wrap">
              <label>{{ $t('reusable.email.label') }}</label>
            </div>
            <input
              type="email"
              name="email"
              :value="formData.email"
              @input="setEmail($event.target.value)"
            />
          </div>
          <div
            v-if="formFieldVisibility.preferredLanguage"
            :class="['input-block', { error: $v.preferredLanguage.$error }]"
          >
            <div class="label-wrap">
              <label>
                {{ $t('snippetTemplate.preferredLanguage.preferredLanguageTitle') }}
              </label>
            </div>

            <dropdown
              :items="getLanguageNames()"
              :device-type="deviceType"
              :mobile-title="
                $t('snippetTemplate.preferredLanguage.preferredLanguageMobileTitle')
              "
              :title="$t('snippetTemplate.preferredLanguage.preferredLanguageTitle')"
              :selected-item="formData.preferredLanguage"
              @select="preferredLanguageSelect"
            />
          </div>

          <div
            v-if="formFieldVisibility.countryOfResidence"
            class="input-block country-wrapper"
          >
            <div class="label-wrap">
              <label>
                {{ $t('snippetTemplate.countryOfResidence.countryOfResidenceTitle') }}
              </label>
            </div>

            <Autocomplete
              clear-on-focus
              starts-with
              :error="$v.countryOfResidence.$error"
              :items="residenceCountries"
              :mobile-title="
                $t('snippetTemplate.countryOfResidence.countryOfResidenceTitle')
              "
              :placeholder="
                $t('snippetTemplate.countryOfResidence.countryOfResidenceTitle')
              "
              :selected-item="formData.countryOfResidence"
              @select="countryOfResidenceSelect"
            />
          </div>

          <div
            v-if="formFieldVisibility.gender"
            :class="['input-block', { error: $v.gender.$error }]"
          >
            <div class="label-wrap">
              <label>{{ $t('reusable.paxTitle.label') }}</label>
            </div>
            <div class="radio-items">
              <radio-item
                tabindex="0"
                :name="'GENDER'"
                :value="'M'"
                :is-checked="formData.gender === 'M'"
                :label="$t('reusable.mr')"
                @change="onRadioChange('M')"
              />
              <radio-item
                tabindex="0"
                :name="'GENDER'"
                :value="'F'"
                :is-checked="formData.gender === 'F'"
                :label="$t('reusable.ms')"
                @change="onRadioChange('F')"
              />
            </div>
          </div>

          <div
            v-if="formFieldVisibility.name"
            :class="['input-block', { error: $v.name.$error }]"
          >
            <div class="label-wrap">
              <label>{{ $t('reusable.firstName.label') }}</label>
            </div>
            <input
              type="text"
              name="fname"
              :value="formData.name"
              @input="onNameChange($event.target.value)"
            />
          </div>

          <div
            v-if="formFieldVisibility.surname"
            :class="['input-block', { error: $v.surname.$error }]"
          >
            <div class="label-wrap">
              <label>{{ $t('reusable.lastName.label') }}</label>
            </div>
            <input
              type="text"
              name="surname"
              :value="formData.surname"
              @input="onSurnameChange($event.target.value)"
            />
          </div>

          <div
            v-if="formFieldVisibility.homeAirport"
            :class="['input-block', { error: $v.homeAirport.$error }]"
          >
            <div class="label-wrap">
              <label>
                {{ $t('snippetTemplate.homeAirport.homeAirportTitle') }}
              </label>
            </div>
            <dropdown
              :items="getDestinations()"
              :device-type="deviceType"
              :mobile-title="$t('snippetTemplate.homeAirport.homeAirportMobileTitle')"
              :title="$t('snippetTemplate.homeAirport.homeAirportTitle')"
              :selected-item="formData.homeAirport"
              @select="onHomeAirportSelect"
            />
          </div>

          <div
            v-if="formFieldVisibility.dreamDestination"
            :class="['input-block', { error: $v.dreamDestination.$error }]"
          >
            <div class="label-wrap">
              <label>
                {{ $t('snippetTemplate.dreamDestination.dreamDestinationTitle') }}
              </label>
            </div>
            <dropdown
              :items="getDestinations()"
              :device-type="deviceType"
              :mobile-title="
                $t('snippetTemplate.dreamDestination.dreamDestinationMobileTitle')
              "
              :title="$t('snippetTemplate.dreamDestination.dreamDestinationTitle')"
              :selected-item="formData.dreamDestination"
              @select="onDreamDestinationSelect"
            />
          </div>

          <div
            v-if="formFieldVisibility.dob"
            :class="['input-block', 'dob', { error: $v.dob.$error }]"
          >
            <div class="label-wrap">
              <label>{{ $t('reusable.dateOfBirth.label') }}</label>
            </div>
            <div class="dropdown-fields">
              <dropdown
                :items="dayList"
                :device-type="deviceType"
                :selected-item="formData.birthDate"
                :mobile-title="$t('reusable.selectDay')"
                :title="$t('reusable.day')"
                class="day"
                @select="selectDay"
              />
              <dropdown
                :items="monthList"
                :device-type="deviceType"
                :mobile-title="$t('reusable.selectMonth')"
                :title="$t('reusable.month')"
                :selected-item="formData.birthMonth"
                class="month"
                @select="selectMonth"
              />
              <dropdown
                :items="yearsList"
                :device-type="deviceType"
                :mobile-title="$t('reusable.selectYear')"
                :title="$t('reusable.year')"
                :selected-item="formData.birthYear"
                class="year"
                @select="selectYear"
              />
            </div>
          </div>

          <div
            v-if="formFieldVisibility.terms"
            :class="['input-block', 'terms-block', { error: $v.terms.$error }]"
          >
            <checkbox
              :checkbox-name="'terms'"
              :checkbox-label="''"
              :is-checked="formData.terms"
              @updated="onTermsUpdated"
            />
            <span
              v-dompurify-html="termsDescription"
              class="terms-description"
            />
          </div>
        </div>
        <div class="button-wrap">
          <button
            class="btn btn-blue"
            type="button"
            @click="subscribe()"
          >
            {{ $t('subscribe.form.button') }}
          </button>
        </div>
      </form>
    </div>

    <small-loader />
  </div>
</template>
<script>
  import { mapState, mapGetters } from 'vuex'
  import SubscribeFormState from 'store/modules/Pages/Subscribe/Form'
  import Checkbox from 'components/Checkbox/Checkbox'
  import Dropdown from 'components/Dropdown/DropdownSimple'
  import RadioItem from 'components/Radio/RadioItemOptional'
  import AbstractStore from 'store/AbstractStore'
  import SmallLoader from 'components/SmallLoader'
  import axios from 'axios'
  import moment from 'moment'
  import divolteSubscribe from 'services/AirBaltic/Divolte/Subscribe'
  import Autocomplete from 'components/Dropdown/Autocomplete'
  import DataLayer from 'services/AirBaltic/DataLayer'

  const FormErrors = () =>
    import(/* webpackChunkName: "form-errors" */ 'components/FormErrors/FormErrors')

  export default {
    components: {
      FormErrors,
      Dropdown,
      RadioItem,
      Checkbox,
      SmallLoader,
      Autocomplete
    },
    extends: AbstractStore,
    props: {
      namespace: {
        type: String,
        required: true
      },
      sectionData: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        yearsList: [{ title: '--', value: '' }],
        monthList: [{ title: '--', value: '' }],
        dayList: [{ title: '--', value: '' }]
      }
    },
    computed: {
      ...mapState('snippetTemplate', ['pageIntName']),
      ...mapState('main', ['selectedLang']),
      $v() {
        return this.abstractGetter('/$v')
      },
      formErrors() {
        return this.abstractGetter('/formErrors')
      },
      description() {
        return this.abstractStateMapper('description')
      },
      title() {
        return this.abstractStateMapper('title')
      },
      image() {
        return this.abstractStateMapper('image')
      },
      refName() {
        return this.abstractStateMapper('refName')
      },
      formData() {
        return this.abstractStateMapper('formData')
      },
      formFieldVisibility() {
        return this.abstractStateMapper('formFieldVisibility')
      },
      preferredLanguages() {
        return this.abstractStateMapper('preferredLanguages')
      },
      residenceCountries() {
        const countries = this.abstractStateMapper('residenceCountries') || {}

        return Object.values(countries).reduce((acc, cur) => {
          return [...acc, ...cur]
        }, [])
      },
      destinations() {
        return this.abstractStateMapper('destinations')
      },
      termsDescription() {
        return this.abstractStateMapper('termsDescription')
      },
      ...mapState('device', ['deviceType']),
      ...mapGetters('device', ['isMobile'])
    },
    mounted() {
      this.createYearsList()
      this.createMonthList()
      this.createDayList()
      this.abstractCommit('/setPreferredLanguage', this.$i18n.locale())
      this.abstractCommit('/setRefName', 'shortForm')
      this.abstractCommit('/setTitle', this.sectionData.title)
      this.abstractCommit('/setImage', this.sectionData.image)
      this.abstractCommit('/setInterest', this.sectionData.interest)
      this.abstractCommit('/setSubscribeSource', this.sectionData.subscribeSource)
      this.abstractCommit('/setDescription', this.sectionData.description)
      this.abstractCommit('/setSref', this.sectionData.subscribeSourceSref)
      this.abstractDispatch('/setVisibleFields', this.sectionData.visibleFields)
      this.abstractCommit('/setPreferredLanguages', this.sectionData.preferredLanguages)
      this.abstractCommit('/setResidenceCountries', this.sectionData.orderedCountries)
      this.abstractCommit('/setTermsDescription', this.sectionData.termsText)
      this.abstractCommit('/setMissingTermsText', this.sectionData.termsMissingText)
      this.abstractDispatch('/setRequiredFields', this.sectionData.requiredFields)

      if (
        this.sectionData.visibleFields.indexOf('homeAirport') !== -1 ||
        this.sectionData.visibleFields.indexOf('dreamDestination') !== -1
      ) {
        axios
          .get(this.sectionData.destinationsUrl)
          .then((response) => {
            this.abstractCommit('/setDestinations', response.data)
          })
          .catch(() => {})
      }
    },
    created() {
      this.$store.registerModule(this.$options.propsData.namespace, SubscribeFormState())
    },
    methods: {
      getFormBackgroundImage() {
        if (this.image !== '' && !this.isMobile) {
          return {
            background: `rgba(0, 0, 0, 0) url(${this.image}) no-repeat scroll 100% 50% / cover`
          }
        }

        return ''
      },
      selectDay(value) {
        this.abstractDispatch('/setBirthDate', value)
      },
      selectMonth(value) {
        this.abstractDispatch('/setBirthMonth', value)
      },
      selectYear(value) {
        this.abstractDispatch('/setBirthYear', value)
      },
      getLanguageNames() {
        const languages = []
        Object.keys(this.preferredLanguages).forEach((value) => {
          languages.push({
            title: value !== '' ? this.$t('languages.' + value) : '',
            value
          })
        })

        return languages
      },
      getDestinations() {
        const destinations = []
        Object.keys(this.destinations).forEach((index) => {
          destinations.push({
            title: this.destinations[`${index}`].name,
            value: index
          })
        })

        return destinations
      },
      preferredLanguageSelect(value) {
        this.abstractCommit('/setPreferredLanguage', value)
      },
      countryOfResidenceSelect(value) {
        this.abstractCommit('/setCountryOfResidence', value)
      },
      onRadioChange(value) {
        this.abstractCommit('/setGender', value)
      },
      onNameChange(value) {
        this.abstractCommit('/setName', value)
      },
      onSurnameChange(value) {
        this.abstractCommit('/setSurname', value)
      },
      onHomeAirportSelect(value) {
        this.abstractCommit('/setHomeAirport', value)
      },
      onDreamDestinationSelect(value) {
        this.abstractCommit('/setDreamDestination', value)
      },
      onTermsUpdated(value) {
        this.abstractCommit('/setTerms', value)
      },
      setEmail(value) {
        this.abstractCommit('/setEmail', value)
      },
      submitForm() {
        this.$store.commit('main/setShowSmallLoader', true)
        this.$store
          .dispatch(this.namespace + '/submitForm')
          .then((subscribeStatus) => {
            this.$emit('subscribed', subscribeStatus, this.namespace),
              DataLayer.doPush({
                event: 'sign_up_form',
                event_category: this.pageIntName,
                event_action: 'submitted',
                site_language: this.selectedLang,
                page_type: this.pageIntName
              })
          })
          .catch(() => {
            DataLayer.doPush({
              event: 'sign_up_form',
              event_category: this.pageIntName,
              event_action: 'error',
              event_message: 'Something went wrong. Please try again later.',
              site_language: this.selectedLang,
              page_type: this.pageIntName
            })
          })
          .finally(() => {
            divolteSubscribe.newsletter(this.formData)
            this.$store.commit('main/setShowSmallLoader', false)
          })
      },
      subscribe() {
        this.$store
          .dispatch(this.namespace + '/validate')
          .then((formValid) => {
            if (formValid) {
              this.submitForm()
            }
          })
          // eslint-disable-next-line handle-callback-err
          .catch(() => {})
      },
      createDayList() {
        for (let i = 1; i <= 31; i++) {
          let day = i
          if (day < 10) {
            day = '0' + day
          }
          this.dayList.push({
            title: day,
            value: day.toString()
          })
        }
      },
      createMonthList() {
        moment.months().forEach((value, i) => {
          this.monthList.push({
            title: value,
            value: i < 9 ? '0' + (i + 1) : i + 1
          })
        })
      },
      createYearsList() {
        const currentYear = new Date().getFullYear() - 16
        for (let i = 0; i <= 100; i++) {
          this.yearsList.push({
            title: currentYear - i,
            value: (currentYear - i).toString()
          })
        }
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .country-wrapper /deep/ {
    .dropdown-list {
      @include bp-mobile {
        top: 100px !important;
      }
    }

    .arrow-down:after {
      font-size: em(12);
      color: $grey-default;
      top: 16px;
    }

    .open input {
      height: 48px !important;
      padding: 0 23px;

      @include bp-mf-tablet {
        padding-left: 16px;
        padding-right: 42px;
        height: 40px !important;
      }
    }

    input::placeholder {
      color: $grey-default;
      font-size: em(12);
    }
  }
</style>
